import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PiArrowCounterClockwise } from 'react-icons/pi';

import { fetchWorkOrderMaterialCandidates } from '#/api/momMgmt/workOrder/fetchMaterialCandidates';
import { StatusBadge } from '#/modules/commonComponents/badge';
import { Button } from '#/modules/commonComponents/button';
import AppDialog from '#/modules/commonComponents/dialog/AppDialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import {
  createFixedMultiFilterMeta,
  createTextFilterMeta,
} from '#/modules/commonComponents/table/columnFilterMeta';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import TableSearchInput from '#/modules/commonComponents/table/TableSearchInput';
import { createSelectColumn } from '#/modules/commonComponents/table/tableColumnHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import {
  BOM_STATUS_FILTER_OPTIONS,
  formatBomStatusLabel,
} from '#/modules/momMgmt/settings/bom/helper/bomStatus';
import useWorkOrderMaterialCandidateListQuery from '#/modules/momMgmt/workOrder/hooks/useWorkOrderMaterialCandidateListQuery';
import { toast } from '#/utils/toast';
import { workOrderMaterialCandidatesQueryKey } from '#/utils/queryKeys';

const SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  mtrNo: '料號',
  name: '名稱',
  bom_name: 'BOM',
  bomStatus: 'BOM 狀態',
};

function formatMaterialCandidateBomLabel(bom) {
  if (!bom) {
    return '-';
  }
  return [bom.name || '—', bom.version ? `(v${bom.version})` : ''].filter(Boolean).join(' ');
}

function isBomSelectable(row) {
  return row?.bom?.status === 'ACTIVE';
}

function mapCandidateRow(row) {
  const mtrId = row?.mtr?.id;
  if (!mtrId) {
    return null;
  }
  return {
    ...row,
    id: mtrId,
  };
}

function buildCandidateColumns({
  filters = {},
  onFilterChange,
} = {}) {
  return [
    createSelectColumn({ enableMultiSelection: false }),
    {
      id: 'mtrNo',
      header: '料號',
      accessorFn: (row) => row.mtr?.mtrNo ?? '-',
      size: 140,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'mtrNo',
          label: '料號',
          filterValue: filters.mtrNo,
          onChange: (value) => onFilterChange('mtrNo', value),
          placeholder: '搜尋料號',
        }),
      },
    },
    {
      id: 'name',
      header: '名稱',
      accessorFn: (row) => row.mtr?.name ?? '-',
      size: 180,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'name',
          label: '名稱',
          filterValue: filters.name,
          onChange: (value) => onFilterChange('name', value),
          placeholder: '搜尋名稱',
        }),
      },
    },
    {
      id: 'bom_name',
      header: 'BOM',
      accessorFn: (row) => formatMaterialCandidateBomLabel(row.bom),
      size: 160,
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        ...createTextFilterMeta({
          key: 'bomName',
          label: 'BOM',
          filterValue: filters.bomName,
          onChange: (value) => onFilterChange('bomName', value),
          placeholder: '搜尋 BOM',
        }),
      },
    },
    {
      id: 'bomStatus',
      header: 'BOM 狀態',
      accessorFn: (row) => row.bom?.status ?? '-',
      size: 110,
      enableSorting: true,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const status = row.original.bom?.status;
        if (!status) {
          return '-';
        }
        return (
          <StatusBadge
            status={status}
            label={formatBomStatusLabel(status)}
            size="sm"
          />
        );
      },
      meta: createFixedMultiFilterMeta({
        key: 'bomStatus',
        label: 'BOM 狀態',
        options: BOM_STATUS_FILTER_OPTIONS,
        filterValue: filters.bomStatus,
        onChange: (value) => onFilterChange('bomStatus', value),
      }),
    },
  ];
}

const WorkOrderMaterialCandidatePickerDialog = ({
  open,
  onOpenChange,
  onConfirm,
  selectedMtrId = '',
  title = '選擇成品',
  description = '僅可確認 BOM 為啟用的候選；停用僅供檢視。',
}) => {
  const {
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetQuery,
  } = useWorkOrderMaterialCandidateListQuery();

  const [selectedById, setSelectedById] = useState({});
  const [searchKeyword, setSearchKeyword] = useState('');

  const listQueryResult = useQuery({
    queryKey: workOrderMaterialCandidatesQueryKey(listQuery),
    queryFn: () => fetchWorkOrderMaterialCandidates(listQuery),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(
    () => (listQueryResult.data?.data ?? []).map(mapCandidateRow).filter(Boolean),
    [listQueryResult.data?.data],
  );

  const preselectedRow = useMemo(() => {
    if (!selectedMtrId) {
      return null;
    }
    return rows.find((row) => String(row.id) === String(selectedMtrId)) ?? null;
  }, [rows, selectedMtrId]);

  const initialRowSelection = useMemo(() => {
    if (!preselectedRow || !isBomSelectable(preselectedRow)) {
      return null;
    }
    return { [String(preselectedRow.id)]: true };
  }, [preselectedRow]);

  const effectiveSelectedById = useMemo(() => {
    if (Object.keys(selectedById).length > 0) {
      return selectedById;
    }
    if (preselectedRow && isBomSelectable(preselectedRow)) {
      return { [String(preselectedRow.id)]: preselectedRow };
    }
    return selectedById;
  }, [preselectedRow, selectedById]);

  const tablePanelKey = preselectedRow?.id
    ? `pick-${preselectedRow.id}`
    : 'pick-default';

  const handleFilterChange = useCallback((key, value) => {
    setFilter(key, value);
  }, [setFilter]);

  const columns = useMemo(() => buildCandidateColumns({
    filters: listQuery.filters,
    onFilterChange: handleFilterChange,
  }), [listQuery.filters, handleFilterChange]);

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns),
    [columns],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowSelectionChange = useCallback((selection) => {
    setSelectedById((prev) => {
      const next = {};
      rows.forEach((row) => {
        const id = String(row.id);
        if (!selection[id]) {
          return;
        }
        if (!isBomSelectable(row)) {
          toast.error('此成品的 BOM 已停用，無法選用');
          return;
        }
        next[id] = row;
      });
      if (Object.keys(next).length === 0 && Object.keys(selection).length > 0) {
        return prev;
      }
      return next;
    });
  }, [rows]);

  function handleSearchSubmit() {
    setKeyword(searchKeyword);
  }

  function handleResetFilters() {
    setSearchKeyword('');
    resetQuery();
  }

  function handleConfirm() {
    const selected = Object.values(effectiveSelectedById).filter(Boolean);
    if (selected.length === 0) {
      toast.error('請選擇一筆成品');
      return;
    }
    const row = selected[0];
    if (!isBomSelectable(row)) {
      toast.error('此成品的 BOM 已停用，無法選用');
      return;
    }
    onConfirm(row);
    onOpenChange(false);
  }

  function handleCancel() {
    onOpenChange(false);
  }

  function handleOpenChange(nextOpen) {
    if (!nextOpen) {
      resetQuery();
      setSearchKeyword('');
      setSelectedById({});
    } else if (selectedMtrId) {
      setFilter('mtrId', selectedMtrId);
    }
    onOpenChange(nextOpen);
  }

  const headerSearch = (
    <div className="flex w-full max-w-md items-center gap-2 sm:w-72">
      <TableSearchInput
        useKeyword
        keyword={searchKeyword}
        onKeywordChange={setSearchKeyword}
        onSearch={handleSearchSubmit}
        placeholder="搜尋料號、名稱"
        className="min-w-0 w-full text-foreground"
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="size-10 shrink-0 bg-panel text-foreground hover:bg-panel/90"
            aria-label="清除搜尋與篩選"
            onClick={handleResetFilters}
          >
            <PiArrowCounterClockwise size={20} aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">清除搜尋與篩選</TooltipContent>
      </Tooltip>
    </div>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      headerVariant="primary"
      headerEnd={headerSearch}
      showCloseButton
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      closeOnOutsideClick={false}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      confirmText="確認選取"
      cancelText="取消"
      cancelAriaLabel="取消選擇成品"
      confirmDisabled={listQueryResult.isLoading}
    >
      <ListTablePanel
        key={tablePanelKey}
        variant="plain"
        columns={columns}
        rows={rows}
        pagination={listQueryResult.data?.page ?? {
          current_page: 1,
          page_size: 10,
          total_count: 0,
          total_page: 1,
        }}
        isLoading={listQueryResult.isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        listQuery={listQuery}
        filterStatusFields={filterStatusFields}
        sortFieldLabels={SORT_FIELD_LABELS}
        onFilterChange={setFilter}
        onKeywordClear={() => {
          setSearchKeyword('');
          setKeyword('');
        }}
        onSortRemove={removeSortAt}
        onSortingChange={handleSortingChange}
        enableMultiSelection={false}
        paginationClassName="px-0 py-2"
        initialRowSelection={initialRowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />
    </AppDialog>
  );
};

WorkOrderMaterialCandidatePickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedMtrId: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default WorkOrderMaterialCandidatePickerDialog;
